import * as React from 'react'
import './index.scss'
import PossessionChart from "../possession-chart";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import _ from 'lodash'
import NewReleasesIcon from '@mui/icons-material/NewReleases';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    id: string,
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, id, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`statistics-tab-${id}`}
            aria-labelledby={`tab-${id}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                     {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

const statistics = (statistics) => {
    let groups = []

    statistics.forEach((stat) => {
        if (!groups.find((el) => el.groupName === stat.groupName)) {
            groups.push({
                groupName: stat.groupName,
                stats: _.cloneDeep(statistics)
                    .filter((el) => el.groupName === stat.groupName)
                    .map((el) => {
                        const homeValue = parseFloat(el.homeValue);
                        const awayValue = parseFloat(el.awayValue);
                        const total = homeValue + awayValue;

                        el.homePercentage = total === 0 ? "0%" : ((homeValue / total) * 100).toFixed(2) + "%";
                        el.awayPercentage = total === 0 ? "0%" : ((awayValue / total) * 100).toFixed(2) + "%";

                        return el
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
            });
        }
    });

    groups = groups.sort((a, b) => {
        if (a.groupName === 'Match overview') {
            return -1;
        }

        return 1;
    })

    return (
        <div className="statistics-options">
            {groups.map((group, index) => (
                <div key={'group-' + index} className="statistics-group">
                    <div className="statistics-group-title">{group.groupName}</div>
                    <div className="statistics-group-content">
                        {group.stats.map((item, index: number) => (
                            <div className="option-wrapper" key={item.name + '-' + index}>
                                <div className="statistics-option">
                                    <div className="home">{item.home}</div>
                                    <div className="name">{item.name}</div>
                                    <div className="away">{item.away}</div>
                                </div>
                                <div className="line">
                                    <div className="home" style={{ width: item.homePercentage }}/>
                                    <div className="away" style={{ width: item.awayPercentage }}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    )
}

export default function GameStatistics(props: any) {
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    if (!props.data) {
        return <div className="no-available-games">
            <NewReleasesIcon/>
            <h4>No statistics available for this game.</h4>
        </div>
    }

    const allStats = props.data.filter((el) => el.eventPeriod === 'ALL');

    // Function to calculate sum based on provided stat names
    const calculateSum = (stats, statNames, valueType) => {
        return stats.filter((el) => statNames.includes(el.name))
            .reduce((sum, item) => {
                return sum + parseInt(item[valueType], 10);
            }, 0);
    };

    // Calculate dangerous stats
    const dangerousStats = ['Shots on target', 'Shots off target', 'Big chances missed'];
    const dangerousHome = calculateSum(allStats, dangerousStats, 'homeValue');
    const dangerousAway = calculateSum(allStats, dangerousStats, 'awayValue');

    // Calculate attack stats
    const attackStats = ['Total shots'];
    const attackHome = calculateSum(allStats, attackStats, 'homeValue');
    const attackAway = calculateSum(allStats, attackStats, 'awayValue');

    // Calculate possession stats
    const possessionStats = ['Ball possession'];
    const possessionHome = calculateSum(allStats, possessionStats, 'homeValue');
    const possessionAway = calculateSum(allStats, possessionStats, 'awayValue');

    // Calculate percentages
    const calculatePercentage = (homeValue, awayValue) => {
        const total = homeValue + awayValue;

        if (total === 0) {
            return {
                home: 50,
                away: 50,
            };
        }

        return {
            home: (homeValue / total) * 100,
            away: (awayValue / total) * 100,
        };
    };

    const dangerousPercentages = calculatePercentage(dangerousHome, dangerousAway);
    const attackPercentages = calculatePercentage(attackHome, attackAway);
    const possessionPercentages = calculatePercentage(possessionHome, possessionAway);

    const overallPossession = [
        { name: 'possession', home: possessionPercentages.home, away: possessionPercentages.away },
        { name: 'attack', home: attackPercentages.home, away: attackPercentages.away },
        { name: 'dangerous', home: dangerousPercentages.home, away: dangerousPercentages.away },
    ];

    return (
        <div className="game-statistics">
            <div className="possession">
                <div className='possession-wrapper'>
                    {
                        overallPossession.map((el: any, index: number) =>
                            <PossessionChart
                                key={'overall-possession-' + index}
                                homePercentage={parseInt(el.home.toFixed(0))}
                                awayPercentage={parseInt(el.away.toFixed(0))}
                                type={el.name}
                            />
                        )
                    }
                </div>
            </div>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label="All" {...a11yProps(0)} />
                    <Tab label="1st half" {...a11yProps(1)} />
                    <Tab label="2st half" {...a11yProps(2)} disabled={!props.data.filter((el) => el.eventPeriod === '2ND').length}/>
                </Tabs>
            </Box>
            <TabPanel value={value} index={0} id='all'>
                {statistics(allStats)}
            </TabPanel>
            <TabPanel value={value} index={1} id='first-half'>
                {statistics(props.data.filter((el) => el.eventPeriod === '1ST'))}
            </TabPanel>
            <TabPanel value={value} index={2} id='second-half'>
                {statistics(props.data.filter((el) => el.eventPeriod === '2ND'))}
            </TabPanel>
        </div>
    )
}
